/**
 * Runtime helpers for the WizButler access model (apps >> modules >> grants).
 */
import { APPS, AppDef, AppGrants, LINKED_MODULES, MODULE_DEPENDENCIES, NavAccessContext, SeedRoleKey, StaffRestaurantGrants } from "../types/Access";

// lookup index for the APPS registry (derived — lives here with the helpers that use it)
export const APP_BY_CODE: Record<string, AppDef> = Object.fromEntries(APPS.map((a) => [a.code, a]));

/** bit value of a module within its app (`1 << index`); 0 if unknown. */
export const moduleBit = (appCode: string, moduleKey: string): number => {
  const app = APP_BY_CODE[appCode];
  if (!app) return 0;
  const i = app.modules.findIndex((m) => m.key === moduleKey);
  return i < 0 ? 0 : 1 << i;
};

/** all-bits mask for an app — a "whole app" grant. */
export const fullAppMask = (appCode: string): number => {
  const app = APP_BY_CODE[appCode];
  return app ? (1 << app.modules.length) - 1 : 0;
};

/** Build an AppGrants from a spec of `{ appCode: "*" | moduleKey[] }` (used by seed templates & tests). */
export const grantsFrom = (spec: { [appCode: string]: "*" | string[] }): AppGrants => {
  const g: AppGrants = {};
  for (const [code, mods] of Object.entries(spec)) {
    if (!APP_BY_CODE[code]) continue;
    g[code] = mods === "*" ? fullAppMask(code) : mods.reduce((m, k) => m | moduleBit(code, k), 0);
  }
  return g;
};

/** the mirror twins of `app:key` */
export const linkedModulesOf = (appCode: string, moduleKey: string): string[] => {
  const self = `${appCode}:${moduleKey}`;
  const out: string[] = [];
  for (const [a, b] of LINKED_MODULES) {
    if (a === self) out.push(b);
    else if (b === self) out.push(a);
  }
  return out;
};

const withModuleBit = (grants: AppGrants, appCode: string, moduleKey: string, on: boolean): AppGrants => {
  const bit = moduleBit(appCode, moduleKey);
  if (!bit) return grants;
  const cur = grants[appCode] || 0;
  return { ...grants, [appCode]: on ? cur | bit : cur & ~bit };
};

export const prerequisitesOf = (appCode: string, moduleKey: string): string[] => {
  const self = `${appCode}:${moduleKey}`;
  return MODULE_DEPENDENCIES.filter(([dep]) => dep === self).map(([, pre]) => pre);
};

export const dependentsOf = (appCode: string, moduleKey: string): string[] => {
  const self = `${appCode}:${moduleKey}`;
  return MODULE_DEPENDENCIES.filter(([, pre]) => pre === self).map(([dep]) => dep);
};

export const prerequisiteLabelsOf = (appCode: string, moduleKey: string): string[] =>
  prerequisitesOf(appCode, moduleKey).map((pre) => {
    const [pa, pk] = pre.split(":");
    return APP_BY_CODE[pa]?.modules.find((m) => m.key === pk)?.name || pre;
  });

export const reconcileModuleDependencies = (grants: AppGrants): AppGrants => {
  let next = { ...grants };
  for (const [dep, pre] of MODULE_DEPENDENCIES) {
    const [da, dk] = dep.split(":");
    const [pa, pk] = pre.split(":");
    const depOn = ((next[da] || 0) & moduleBit(da, dk)) !== 0;
    const preOn = ((next[pa] || 0) & moduleBit(pa, pk)) !== 0;
    if (depOn && !preOn) next = withModuleBit(next, da, dk, false);
  }
  return next;
};

export const setModuleGrant = (grants: AppGrants, appCode: string, moduleKey: string, on: boolean): AppGrants => {
  let next = withModuleBit(grants, appCode, moduleKey, on);
  for (const twin of linkedModulesOf(appCode, moduleKey)) {
    const [ta, tk] = twin.split(":");
    next = withModuleBit(next, ta, tk, on);
  }
  if (on) {
    for (const pre of prerequisitesOf(appCode, moduleKey)) {
      const [pa, pk] = pre.split(":");
      next = withModuleBit(next, pa, pk, true);
    }
  } else {
    for (const dep of dependentsOf(appCode, moduleKey)) {
      const [da, dk] = dep.split(":");
      next = withModuleBit(next, da, dk, false);
    }
  }
  return next;
};

export const reconcileAppLinks = (grants: AppGrants, appCode: string): AppGrants => {
  const app = APP_BY_CODE[appCode];
  if (!app) return grants;
  let next = grants;
  for (const m of app.modules) {
    const on = ((next[appCode] || 0) & moduleBit(appCode, m.key)) !== 0;
    for (const twin of linkedModulesOf(appCode, m.key)) {
      const [ta, tk] = twin.split(":");
      next = withModuleBit(next, ta, tk, on);
    }
  }
  return next;
};

export const reconcileLinkedGrants = (grants: AppGrants): AppGrants => {
  let next = { ...grants };
  for (const [a, b] of LINKED_MODULES) {
    const [aa, ak] = a.split(":");
    const [ba, bk] = b.split(":");
    const aOn = ((next[aa] || 0) & moduleBit(aa, ak)) !== 0;
    const bOn = ((next[ba] || 0) & moduleBit(ba, bk)) !== 0;
    if (aOn !== bOn) {
      next = withModuleBit(next, aa, ak, true);
      next = withModuleBit(next, ba, bk, true);
    }
  }
  return next;
};

/** Module membership check for FE + BE (no bitwise in Firestore rules). */
export const canAccessModule = (grants: AppGrants | undefined, appCode: string, moduleKey: string): boolean => {
  if (!grants) return false;
  const bit = moduleBit(appCode, moduleKey);
  return bit !== 0 && ((grants[appCode] || 0) & bit) !== 0;
};

/** restaurant ids where the caller holds ANY grant in `appCode` (app-level access; bitmask > 0). e.g. reporting = `"rp"`. */
export const restaurantsWithApp = (staffRestaurants: StaffRestaurantGrants | undefined, appCode: string): string[] => {
  const m = staffRestaurants || {};
  return Object.keys(m).filter((rid) => ((m[rid]?.grants || {})[appCode] || 0) > 0);
};

/** restaurant ids where the caller holds a SPECIFIC module grant (`appCode:moduleKey`). e.g. Maestro analytics = `"ms","analytics"`. */
export const restaurantsWithModule = (staffRestaurants: StaffRestaurantGrants | undefined, appCode: string, moduleKey: string): string[] => {
  const m = staffRestaurants || {};
  return Object.keys(m).filter((rid) => canAccessModule(m[rid]?.grants, appCode, moduleKey));
};

/** Single source of truth for FE nav/route gating, shared by EVERY app (Maestro, Office, Marketing…). */
export const canSeeNavItem = (item: { minRole?: number; module?: string }, ctx: NavAccessContext): boolean => {
  if (ctx.isSystem) return true;
  if (item.minRole != null && (ctx.roles || 0) < item.minRole) return false;
  if (item.module) {
    const [appCode, moduleKey] = item.module.split(":");
    if (!canAccessModule(ctx.grants, appCode, moduleKey)) return false;
  }
  return true;
};

/** Section-level gate (sections carry only an optional `minRole`, no module). */
export const canSeeNavSection = (section: { minRole?: number }, ctx: NavAccessContext): boolean => !!ctx.isSystem || section.minRole == null || (ctx.roles || 0) >= section.minRole;

// code seed templates per role (tenant/restaurant-agnostic); seeded on client onboarding, then tenant ADMINs tune
// role definitions via Maestro Admin > User & Access.
export const ROLE_SEED_TEMPLATES: Record<SeedRoleKey, AppGrants> = {
  station: reconcileLinkedGrants(
    grantsFrom({
      ms: ["bookings", "till", "panorama", "customers"],
      pd: "*",
    })
  ),
  supervisor: reconcileLinkedGrants(
    grantsFrom({
      ms: ["bookings", "till", "panorama", "customers"],
      pd: "*",
      of: ["products"],
      mk: ["giftCertificates", "vouchers"],
    })
  ),
  restaurantManager: reconcileLinkedGrants(
    grantsFrom({
      ms: ["bookings", "till", "panorama", "customers"],
      st: ["generalSetup", "floorPlanSetup", "serviceSetup", "orderingSetup", "functionSetup", "postLabels"],
      pd: "*",
      of: ["products"],
      mk: "*",
      rp: ["coverReports", "customerReports", "productsReports", "functionReports", "operatorReports", "customReports"],
      vc: "*",
    })
  ),
  admin: reconcileLinkedGrants(grantsFrom({ ms: "*", st: ["userSetup", "generalSetup", "floorPlanSetup", "serviceSetup", "orderingSetup", "functionSetup", "postLabels", "billingReports"], pd: "*", of: "*", ac: "*", mk: "*", rp: "*", vc: "*" })),
};

/** Signature of the current grantable registry — "app:key" */
export const registrySignature = (): string[] => APPS.flatMap((a) => a.modules.filter((m) => !m.systemOnly).map((m) => `${a.code}:${m.key}`)).sort();

/** the code seed templates as a role->grants map (used to seed/re-seed a restaurant's role definitions). */
export const seedRoleDefinitions = (): Record<SeedRoleKey, AppGrants> => ({
  station: { ...ROLE_SEED_TEMPLATES.station },
  supervisor: { ...ROLE_SEED_TEMPLATES.supervisor },
  restaurantManager: { ...ROLE_SEED_TEMPLATES.restaurantManager },
  admin: { ...ROLE_SEED_TEMPLATES.admin },
});
